<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
    <div class="container mx-auto px-4 py-8">
      <!-- Report Header -->
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-customHafadhColor rounded-full mb-4">
          <i class="fas fa-chart-line text-white text-2xl"></i>
        </div>
        <h1 class="text-4xl font-bold text-gray-800 mb-2">تقرير ردود الجلسات</h1>
        <p class="text-gray-600 text-lg">تحليل شامل لأداء المعلمين وتفاعلهم مع الدارسين</p>
      </div>
      
      <!-- Filters Section -->
      <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
        <div class="flex items-center mb-6">
          <div class="w-1 h-8 bg-customHafadhColor rounded-full ml-3"></div>
          <h3 class="text-xl font-semibold text-gray-800">خيارات التصفية</h3>
        </div>
        
        <!-- Filters Row -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-6">
          <!-- Session Selection -->
          <div class="lg:col-span-4">
            <label class="block text-sm font-semibold text-gray-700 mb-3">
              <i class="fas fa-user-tie ml-2 text-customHafadhColor"></i>
              اختر الجلسة
            </label>
            <div class="relative">
              <select 
                v-model="selectedTeacherId"
                @change="loadTeacherData"
                class="w-full px-4 py-3 pr-12 bg-white border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor focus:border-customHafadhColor transition-all duration-200 appearance-none">
                <option value="">اختر الجلسة</option>
                <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                  {{ teacher.fullName }}
                </option>
              </select>
              <i class="fas fa-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>

          <!-- Organization Selection -->
          <div class="lg:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-3">
              <i class="fas fa-building ml-2 text-customHafadhColor"></i>
              الجهة
            </label>
            <div class="relative">
              <select 
                v-model="selectedOrganizationId"
                class="w-full px-4 py-3 pr-12 bg-white border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor focus:border-customHafadhColor transition-all duration-200 appearance-none">
                <option value="">اختر الجهة</option>
                <option v-for="org in organizations" :key="org.id" :value="org.id">
                  {{ org.name }}
                </option>
              </select>
              <i class="fas fa-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>

          <!-- Date From -->
          <div class="lg:col-span-3">
            <label class="block text-sm font-semibold text-gray-700 mb-3">
              <i class="fas fa-calendar-alt ml-2 text-customHafadhColor"></i>
              من تاريخ
            </label>
            <input
              type="date"
              v-model="filters.dateFrom"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor focus:border-customHafadhColor transition-all duration-200"
            />
          </div>

          <!-- Date To -->
          <div class="lg:col-span-3">
            <label class="block text-sm font-semibold text-gray-700 mb-3">
              <i class="fas fa-calendar-check ml-2 text-customHafadhColor"></i>
              إلى تاريخ
            </label>
            <input
              type="date"
              v-model="filters.dateTo"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor focus:border-customHafadhColor transition-all duration-200"
            />
          </div>
        </div>

        <!-- Action Buttons Row -->
        <div class="flex flex-row-reverse gap-4 justify-start">
          <!-- Generate Report Button (Primary Action) -->
          <button
            @click="generateReport"
            :disabled="!selectedTeacherId || loading"
            class="px-4 py-3 bg-gradient-to-r from-customHafadhColor to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
            title="عرض التقرير"
          >
            <i class="fas fa-chart-line text-lg"></i>
          </button>
          
          <!-- PDF Export Button -->
          <button
            v-if="reportData.length > 0"
            @click="exportToPDF"
            :disabled="loading"
            class="px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
            title="تحميل HTML"
          >
            <i class="fas fa-file-code text-lg"></i>
          </button>
          
          <!-- Excel/CSV Export Button -->
          <button
            v-if="reportData.length > 0"
            @click="exportToExcel"
            :disabled="loading"
            class="px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
            title="تحميل CSV"
          >
            <i class="fas fa-file-csv text-lg"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="relative">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-gray-300"></div>
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-customHafadhColor border-t-transparent absolute top-0 left-0"></div>
        </div>
        <p class="mt-6 text-lg text-gray-600 font-medium">جاري التحميل...</p>
        <div class="mt-2 flex space-x-1">
          <div class="w-2 h-2 bg-customHafadhColor rounded-full animate-bounce"></div>
          <div class="w-2 h-2 bg-customHafadhColor rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
          <div class="w-2 h-2 bg-customHafadhColor rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-2xl p-6 mb-8 shadow-lg">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
              <i class="fas fa-exclamation-triangle text-white"></i>
            </div>
          </div>
          <div class="mr-4">
            <h3 class="text-lg font-semibold text-red-800">حدث خطأ!</h3>
            <p class="text-red-700 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Report Content -->
      <div v-if="reportData.length > 0 && !loading" class="teacher-response-report">
        <!-- PDF Header with Logos (hidden on screen, visible in PDF) -->
        <div class="pdf-header" style="display: none;">
          <div class="header-logos">
            <div class="left-logo">
              <!-- App Logo -->
              <img :src="appLogo" alt="شعار التطبيق" class="app-logo" />
            </div>
            <div class="center-title">
              <h1 class="pdf-title">تقرير ردود الأساتذة</h1>
              <p class="pdf-subtitle">{{ selectedTeacherName }} - {{ formattedDateRange }}</p>
            </div>
            <div class="right-logo">
              <img v-if="selectedOrganization && (selectedOrganization.logoUrl || selectedOrganization.logoBase64)" 
                   :src="selectedOrganization.logoUrl || `data:image/jpeg;base64,${selectedOrganization.logoBase64}`" 
                   :alt="selectedOrganization.name"
                   class="organization-logo" />
            </div>
          </div>
          <hr class="header-divider" />
        </div>
        
        <!-- Teacher Info Header -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
          <div class="text-center mb-8">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-customHafadhColor to-blue-700 rounded-full mb-4">
              <i class="fas fa-user-graduate text-white text-3xl"></i>
            </div>
            <h2 class="text-3xl font-bold text-gray-800 mb-2">{{ selectedTeacherName }}</h2>
            <p class="text-gray-600 text-lg bg-gray-100 px-4 py-2 rounded-full inline-block">{{ formattedDateRange }}</p>
          </div>
          
          <!-- Summary Stats -->
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-3xl font-bold text-blue-700 mb-1">{{ summary.totalSessions }}</div>
                  <div class="text-sm font-medium text-blue-600">إجمالي الجلسات</div>
                </div>
                <div class="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <i class="fas fa-calendar text-white text-lg"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-3xl font-bold text-green-700 mb-1">{{ summary.completedSessions }}</div>
                  <div class="text-sm font-medium text-green-600">الجلسات المكتملة</div>
                </div>
                <div class="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <i class="fas fa-check-circle text-white text-lg"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border border-yellow-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-3xl font-bold text-yellow-700 mb-1">{{ summary.averageRating.toFixed(1) }}</div>
                  <div class="text-sm font-medium text-yellow-600">متوسط التقييم</div>
                </div>
                <div class="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <i class="fas fa-star text-white text-lg"></i>
                </div>
              </div>
            </div>
            
            <div class="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-3xl font-bold text-purple-700 mb-1">{{ summary.totalStudents }}</div>
                  <div class="text-sm font-medium text-purple-600">عدد الدارسين</div>
                </div>
                <div class="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <i class="fas fa-users text-white text-lg"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sessions Table -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div class="px-8 py-6 bg-gradient-to-r from-customHafadhColor to-blue-700 text-white">
            <div class="flex items-center">
              <i class="fas fa-table text-2xl ml-4"></i>
              <h3 class="text-2xl font-bold">تفاصيل الجلسات والردود</h3>
            </div>
          </div>
          
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-user ml-2 text-customHafadhColor"></i>اسم الدارس
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-target ml-2 text-customHafadhColor"></i>الهدف
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-book ml-2 text-customHafadhColor"></i>الورد اليومي
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-flag ml-2 text-customHafadhColor"></i>الحالة
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-comment ml-2 text-customHafadhColor"></i>ملاحظات الشيخ
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-clock ml-2 text-customHafadhColor"></i>الوقت
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-calendar ml-2 text-customHafadhColor"></i>التاريخ
                  </th>
                  <th class="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-star ml-2 text-customHafadhColor"></i>التقييم
                  </th>
                  <th class="px-4 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    <i class="fas fa-edit ml-2 text-customHafadhColor"></i>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="(session, index) in reportData" :key="session.id" 
                    :class="index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'"
                    class="transition-all duration-200 hover:shadow-md">
                  <td class="px-6 py-5 whitespace-nowrap border-b border-gray-100">
                    <div class="text-sm font-bold text-gray-900">{{ session.studentName }}</div>
                  </td>
                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100 font-medium">
                    {{ session.goalDescription || 'غير محدد' }}
                  </td>
                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100">
                    <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                      {{ session.pagesReviewed || 'غير محدد' }}
                    </span>
                  </td>
                  <td class="px-6 py-5 whitespace-nowrap border-b border-gray-100">
                    <span :class="getStatusClass(session.status)" class="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full shadow-sm">
                      {{ getStatusText(session.status) }}
                    </span>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-700 max-w-xs border-b border-gray-100">
                    <div class="truncate bg-gray-100 px-3 py-2 rounded-lg" :title="session.teacherNotes">
                      {{ session.teacherNotes || 'لا توجد ملاحظات' }}
                    </div>
                  </td>
                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100 font-medium">
                    <div class="flex items-center">
                      <i class="fas fa-clock text-customHafadhColor ml-2"></i>
                      {{ formatTime(session.sessionTime) }}
                    </div>
                  </td>
                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100 font-medium">
                    <div class="flex items-center">
                      <i class="fas fa-calendar text-customHafadhColor ml-2"></i>
                      {{ formatDate(session.sessionDate) }}
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100">
                    <div class="flex items-center justify-center">
                      <span :class="getRatingBadgeClass(session.rating || 0)" 
                            class="px-3 py-1 text-sm font-bold rounded-lg shadow-sm border transition-all duration-200">
                        {{ session.rating || 0 }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-b border-gray-100 text-center">
                    <button @click="openDetails(session)" class="text-blue-600 hover:text-blue-800">
                      <i class="fas fa-edit"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
          </table>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.totalPages > 1" class="mt-8 flex justify-center">
            <nav class="flex space-x-reverse space-x-2 bg-white rounded-2xl shadow-lg border border-gray-200 p-2">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="changePage(page)"
                :class="page === pagination.currentPage ? 'bg-gradient-to-r from-customHafadhColor to-blue-700 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'"
                class="px-4 py-2 rounded-xl transition-all duration-300 font-medium text-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                {{ page }}
              </button>
            </nav>
          </div>
        </div>

        <!-- No Data State -->
        <div v-else-if="!loading && selectedTeacherId && reportData.length === 0" class="text-center py-20">
          <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-12 max-w-md mx-auto">
            <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fas fa-chart-bar text-4xl text-gray-400"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-800 mb-3">لا توجد بيانات</h3>
            <p class="text-gray-600 text-lg">لا توجد بيانات للفترة المحددة</p>
            <div class="mt-6">
              <button 
                @click="generateReport"
                class="px-6 py-3 bg-gradient-to-r from-customHafadhColor to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <i class="fas fa-refresh ml-2"></i>
                إعادة المحاولة
              </button>
            </div>
          </div>
        </div>

        <!-- Initial State -->
        <div v-else-if="!loading && !selectedTeacherId" class="text-center py-20">
          <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-12 max-w-md mx-auto">
            <div class="w-24 h-24 bg-gradient-to-br from-customHafadhColor to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fas fa-user-tie text-4xl text-white"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-800 mb-3">ابدأ التحليل</h3>
            <p class="text-gray-600 text-lg">الرجاء اختيار جلسة لعرض التقرير</p>
            <div class="mt-6 text-sm text-gray-500">
              <i class="fas fa-arrow-up ml-2"></i>
              اختر الجلسة من القائمة أعلاه
            </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div v-if="details.open" class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div class="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" dir="rtl">
        <div class="px-6 py-4 bg-gradient-to-r from-customHafadhColor to-blue-700 text-white flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-info-circle text-xl ml-3"></i>
            <h3 class="text-xl font-bold">تفاصيل الجلسة</h3>
          </div>
          <div class="flex items-center gap-3">
            <button v-if="!details.edit" @click="beginEdit" class="text-white hover:text-gray-200" title="تعديل">
              <i class="fas fa-edit text-lg"></i>
            </button>
            <button @click="closeDetails" class="text-white hover:text-gray-200" title="إغلاق">
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>
        <div class="p-6 max-h-[70vh] overflow-y-auto">
          <div v-if="details.loading" class="text-center py-10">
            <i class="fas fa-spinner fa-spin text-2xl text-customHafadhColor"></i>
          </div>
          <div v-else>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-for="field in fieldSchema" :key="field.key" class="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div class="text-xs text-gray-500 mb-1">{{ field.label }}</div>
                <template v-if="!details.edit">
                  <div class="text-sm font-semibold text-gray-800 break-words">{{ displayValue(field.key) }}</div>
                </template>
                <template v-else>
                  <input v-if="field.type==='number'" :disabled="field.readonly" type="number" class="w-full border rounded px-2 py-1 text-sm" v-model.number="editable[field.key]" />
                  <input v-else-if="field.type==='text'" :disabled="field.readonly" type="text" class="w-full border rounded px-2 py-1 text-sm" v-model="editable[field.key]" />
                  <textarea v-else-if="field.type==='textarea'" :disabled="field.readonly" rows="2" class="w-full border rounded px-2 py-1 text-sm" v-model="editable[field.key]"></textarea>
                  <label v-else-if="field.type==='boolean'" class="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" v-model="editable[field.key]" :disabled="field.readonly" />
                    <span>{{ editable[field.key] ? 'مفعل' : 'غير مفعل' }}</span>
                  </label>
                  <input v-else :disabled="field.readonly" type="text" class="w-full border rounded px-2 py-1 text-sm" v-model="editable[field.key]" />
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-200 text-left flex justify-between">
          <div>
            <button v-if="details.edit" @click="saveEdits" class="px-4 py-2 bg-customHafadhColor text-white rounded-lg hover:opacity-90 ml-2">حفظ</button>
            <button v-if="details.edit" @click="cancelEdits" class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800">إلغاء</button>
          </div>
          <button @click="closeDetails" class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800">إغلاق</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import url from '../url'
import appLogo from '@/assets/logo.png'

export default {
  name: 'TeacherResponseReport',
  data() {
    return {
      loading: false,
      error: null,
      teachers: [],
      organizations: [],
      selectedTeacherId: '',
      selectedTeacherName: '',
      selectedOrganizationId: '',
      reportData: [],
      appLogo: appLogo,
      filters: {
        dateFrom: '',
        dateTo: ''
      },
      summary: {
        totalSessions: 0,
        completedSessions: 0,
        averageRating: 0,
        totalStudents: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        pageSize: 20
      },
      details: { open: false, loading: false, record: null, edit: false },
      editable: {}
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const total = this.pagination.totalPages
      const current = this.pagination.currentPage
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        }
      }
      
      return pages
    },
    selectedOrganization() {
      return this.organizations.find(org => org.id === this.selectedOrganizationId)
    },
    formattedDateRange() {
      const from = this.filters.dateFrom ? this.formatGregorian(this.filters.dateFrom) : '';
      const to = this.filters.dateTo ? this.formatGregorian(this.filters.dateTo) : '';
      return `من ${from} إلى ${to}`;
    },
    detailsList() {
      const r = this.details?.record || {}
      const fmt = (v) => (v === null || v === undefined || v === '') ? '—' : v
      return [
        { key: 'id', label: 'رقم المتابعة', value: fmt(r.id) },
        { key: 'sessionNumber', label: 'رقم الحلقة', value: fmt(r.sessionNumber) },
        { key: 'date', label: 'تاريخ الجلسة', value: fmt(r.date) },
        { key: 'sessionsTimeFrom', label: 'بداية الوقت', value: fmt(r.sessionsTimeFrom) },
        { key: 'sessionsTimeTo', label: 'نهاية الوقت', value: fmt(r.sessionsTimeTo) },
        { key: 'studentName', label: 'اسم الدارس', value: fmt(r.studentName) },
        { key: 'studentId', label: 'رقم الدارس', value: fmt(r.studentId) },
        { key: 'teacherName', label: 'اسم الشيخ', value: fmt(r.teacherName) },
        { key: 'teacherId', label: 'رقم الشيخ', value: fmt(r.teacherId) },
        { key: 'teacherEvaluation', label: 'تقييم الشيخ', value: fmt(r.teacherEvaluation) },
        { key: 'teacherNote', label: 'ملاحظات الشيخ', value: fmt(r.teacherNote) },
        { key: 'studentEvaluation', label: 'تقييم الدارس', value: fmt(r.studentEvaluation) },
        { key: 'studentNote', label: 'ملاحظات الدارس', value: fmt(r.studentNote) },
        { key: 'pageMemorisedFrom', label: 'من صفحة (حفظ)', value: fmt(r.pageMemorisedFrom) },
        { key: 'pageMemorisedTo', label: 'إلى صفحة (حفظ)', value: fmt(r.pageMemorisedTo) },
        { key: 'pageRecitedFrom', label: 'من صفحة (تلاوة)', value: fmt(r.pageRecitedFrom) },
        { key: 'pageRecitedTo', label: 'إلى صفحة (تلاوة)', value: fmt(r.pageRecitedTo) },
        { key: 'selfReviewCount', label: 'عدد المراجعات الذاتية', value: fmt(r.selfReviewCount) },
        { key: 'isCancelled', label: 'ملغاة؟', value: r.isCancelled ? 'نعم' : 'لا' },
        { key: 'isSessionAvailable', label: 'جلسة متاحة؟', value: r.isSessionAvailable ? 'نعم' : 'لا' },
        { key: 'wasOriginallyAvailableSession', label: 'كانت متاحة أصلاً؟', value: r.wasOriginallyAvailableSession ? 'نعم' : 'لا' },
        { key: 'studentPunchIn', label: 'دخول الدارس', value: fmt(r.studentPunchIn) },
        { key: 'studentPunchOut', label: 'خروج الدارس', value: fmt(r.studentPunchOut) },
        { key: 'teacherPunchIn', label: 'دخول الشيخ', value: fmt(r.teacherPunchIn) },
        { key: 'teacherPunchOut', label: 'خروج الشيخ', value: fmt(r.teacherPunchOut) },
        { key: 'sessionRecording', label: 'رابط التسجيل', value: fmt(r.sessionRecording) },
      ]
    },
    editableFieldKeys() {
      return this.fieldSchema.filter(field => !field.readonly).map(field => field.key)
    },
    fieldSchema() {
      return [
        { key: 'id', label: 'رقم المتابعة', type: 'text', readonly: true },
        { key: 'sessionNumber', label: 'رقم الحلقة', type: 'text', readonly: true },
        { key: 'date', label: 'تاريخ الجلسة', type: 'text', readonly: true },
        { key: 'sessionsTimeFrom', label: 'بداية الوقت', type: 'text', readonly: true },
        { key: 'sessionsTimeTo', label: 'نهاية الوقت', type: 'text', readonly: true },
        { key: 'studentName', label: 'اسم الدارس', type: 'text', readonly: true },
        { key: 'studentId', label: 'رقم الدارس', type: 'number', readonly: true },
        { key: 'teacherName', label: 'اسم الشيخ', type: 'text', readonly: true },
        { key: 'teacherId', label: 'رقم الشيخ', type: 'number', readonly: true },
        { key: 'teacherEvaluation', label: 'تقييم الشيخ', type: 'number', readonly: false },
        { key: 'teacherNote', label: 'ملاحظات الشيخ', type: 'textarea', readonly: false },
        { key: 'studentEvaluation', label: 'تقييم الدارس', type: 'number', readonly: false },
        { key: 'studentNote', label: 'ملاحظات الدارس', type: 'textarea', readonly: false },
        { key: 'pageMemorisedFrom', label: 'من صفحة (حفظ)', type: 'number', readonly: false },
        { key: 'pageMemorisedTo', label: 'إلى صفحة (حفظ)', type: 'number', readonly: false },
        { key: 'pageRecitedFrom', label: 'من صفحة (تلاوة)', type: 'number', readonly: false },
        { key: 'pageRecitedTo', label: 'إلى صفحة (تلاوة)', type: 'number', readonly: false },
        { key: 'selfReviewCount', label: 'عدد المراجعات الذاتية', type: 'number', readonly: false },
        { key: 'isCancelled', label: 'ملغاة؟', type: 'boolean', readonly: false },
        { key: 'isSessionAvailable', label: 'جلسة متاحة؟', type: 'boolean', readonly: false },
        { key: 'wasOriginallyAvailableSession', label: 'كانت متاحة أصلاً؟', type: 'boolean', readonly: false },
        { key: 'studentPunchIn', label: 'دخول الدارس (yyyy-MM-ddTHH:mm:ss)', type: 'text', readonly: true },
        { key: 'studentPunchOut', label: 'خروج الدارس (yyyy-MM-ddTHH:mm:ss)', type: 'text', readonly: true },
        { key: 'teacherPunchIn', label: 'دخول الشيخ (yyyy-MM-ddTHH:mm:ss)', type: 'text', readonly: true },
        { key: 'teacherPunchOut', label: 'خروج الشيخ (yyyy-MM-ddTHH:mm:ss)', type: 'text', readonly: true },
        { key: 'sessionRecording', label: 'رابط التسجيل', type: 'text', readonly: true },
      ]
    }
  },
  async mounted() {
    await this.loadTeachers()
    await this.loadOrganizations()
    this.setDefaultDates()
  },
  methods: {
    displayValue(key) {
      const r = this.details?.record || {}
      const v = r[key]
      if (typeof v === 'boolean') return v ? 'مفعل' : 'غير مفعل'
      if (v === null || v === undefined || v === '') return '—'
      return v
    },
    openDetails(session) {
      this.details.open = true
      this.details.loading = true
      this.details.record = null
      const trackerId = session.sessionId || session.id
      axios.get(process.env.VUE_APP_SERVICE_URL + url.daily_tracker_details + trackerId, {
        headers: { 'X-Authorization': localStorage.getItem('token') }
      }).then(resp => {
        if (resp.data?.result?.status === 200) {
          this.details.record = resp.data.result.data
        }
      }).catch(() => {
        this.details.record = null
      }).finally(() => {
        this.details.loading = false
      })
    },
    closeDetails() {
      this.details.open = false
      this.details.record = null
      this.details.edit = false
      this.editable = {}
    },
    cancelEdits() {
      this.details.edit = false
      this.editable = {}
    },
    beginEdit() {
      if (!this.details.record) return
      const snapshot = {}
      this.fieldSchema.forEach(field => {
        let value = this.details.record[field.key]
        if (field.type === 'boolean') {
          value = Boolean(value)
        }
        snapshot[field.key] = value
      })
      this.editable = snapshot
      this.details.edit = true
    },
    async saveEdits() {
      try {
        const id = this.details.record?.id
        if (!id) return;
        const payload = {}
        this.fieldSchema.filter(field => !field.readonly).forEach(field => {
          const key = field.key
          const currentValue = this.details.record[key]
          const newValue = this.editable[key]
          if (newValue !== currentValue) {
            payload[key] = newValue
          }
        })
        if (Object.keys(payload).length === 0) {
          this.details.edit = false
          return
        }
        const resp = await axios.put(process.env.VUE_APP_SERVICE_URL + url.update_daily_tracker + id, payload, {
          headers: { 'X-Authorization': localStorage.getItem('token') }
        })
        if (resp.data?.result?.status === 200) {
          this.details.record = resp.data.result.data
          this.details.edit = false
          this.editable = {}
          this.generateReport()
          alert('تم حفظ التعديلات')
        }
      } catch (e) {
        console.error('Error saving tracker edits:', e)
        alert('فشل حفظ التعديلات')
      }
    },
    async loadTeachers() {
      try {
        this.loading = true
        console.log('API URL:', process.env.VUE_APP_SERVICE_URL + url.get_all_teachers)
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.get_all_teachers, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
          params: { enabled: true }
        })
        
        if (response && response.data && response.data.result && response.data.result.status === 200) {
          this.teachers = response.data.result.data || []
        } else if (response && response.data && response.data.status === 200) {
          this.teachers = response.data.data || []
        } else if (response && response.data && Array.isArray(response.data)) {
          this.teachers = response.data
        }
      } catch (error) {
        console.error('Error loading teachers:', error)
        this.error = 'فشل في تحميل قائمة المعلمين'
      } finally {
        this.loading = false
      }
    },
    
    async loadOrganizations() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.organizations, {
          headers: { 'X-Authorization': localStorage.getItem('token') }
        })
        
        if (response && response.data && response.data.result && response.data.result.status === 200) {
          this.organizations = response.data.result.data || []
        } else if (response && response.data && response.data.status === 200) {
          this.organizations = response.data.data || []
        } else if (response && response.data && Array.isArray(response.data)) {
          this.organizations = response.data
        }
      } catch (error) {
        console.error('Error loading organizations:', error)
        // Don't show error for organizations as it's optional
      }
    },
    
    loadTeacherData() {
      const selectedTeacher = this.teachers.find(t => t.id === parseInt(this.selectedTeacherId))
      this.selectedTeacherName = selectedTeacher ? selectedTeacher.fullName : ''
      this.reportData = []
      this.resetSummary()
    },
    
    setDefaultDates() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      const todayString = `${year}-${month}-${day}`
      
      this.filters.dateTo = todayString
      this.filters.dateFrom = todayString
    },
    
    async generateReport() {
      if (!this.selectedTeacherId) {
        this.error = 'الرجاء اختيار جلسة'
        return
      }
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.teacher_response_report, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
          params: {
            teacherId: this.selectedTeacherId,
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo,
            page: this.pagination.currentPage - 1,
            size: this.pagination.pageSize
          }
        })
        
        if (response.data.result.status === 200) {
          const data = response.data.result.data
          // Remove duplicate sessions (keep first for each sessionId)
          const sessions = data.sessions || []
          const unique = sessions.filter((item, index, arr) => arr.findIndex(i => i.sessionId === item.sessionId) === index)
          this.reportData = unique
          this.summary = data.summary || this.resetSummary()
          this.pagination.totalPages = data.totalPages || 1
        } else {
          this.error = response.data.result.messageAr || 'فشل في تحميل التقرير'
        }
      } catch (error) {
        console.error('Error generating report:', error)
        this.error = 'حدث خطأ أثناء تحميل التقرير'
      } finally {
        this.loading = false
      }
    },
    
    async exportToPDF() {
      try {
        this.loading = true
        
        // Get ALL data for export (not paginated)
        const allData = await this.getAllReportData()
        
        // Generate HTML content with all data and logos included
        const htmlContent = await this.generateHTMLWithLogos(allData)
        
        // Create HTML file with logos
        const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `تقرير_ردود_${this.selectedTeacherName}_${this.filters.dateFrom}_${this.filters.dateTo}.html`
        link.click()
        
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        console.error('Error exporting HTML:', error)
        this.error = 'فشل في تحميل التقرير'
      } finally {
        this.loading = false
      }
    },
    
    async generateHTMLWithLogos(allData = null) {
      const orgLogo = this.selectedOrganization && (this.selectedOrganization.logoUrl || this.selectedOrganization.logoBase64) 
        ? (this.selectedOrganization.logoUrl || `data:image/jpeg;base64,${this.selectedOrganization.logoBase64}`)
        : ''
      
      // Convert app logo to base64
      const appLogoBase64 = await this.getImageAsBase64(this.appLogo)
      
      // Use provided data or current reportData
      const dataToExport = allData || this.reportData
      
      let html = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>تقرير ردود الأساتذة - ${this.selectedTeacherName}</title>
    <style>
        body {
            font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;
            direction: rtl;
            margin: 20px;
            line-height: 1.6;
            color: #333;
        }
                 .header-section {
             display: flex;
             align-items: center;
             justify-content: space-between;
             margin-bottom: 30px;
             padding: 20px 0;
             border-bottom: 3px solid #c9a06c;
             min-height: 120px;
         }
         .left-logo, .right-logo {
             flex: 0 0 150px;
             display: flex;
             align-items: center;
             justify-content: center;
             padding: 10px;
         }
         .logo {
             max-width: 120px;
             max-height: 120px;
             object-fit: contain;
             border-radius: 8px;
             box-shadow: 0 2px 8px rgba(0,0,0,0.1);
         }
         .center-title {
             flex: 1;
             text-align: center;
             padding: 0 30px;
         }
                 .main-title {
             font-size: 32px;
             font-weight: bold;
             color: #c9a06c;
             margin: 0 0 12px 0;
             text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
         }
         .subtitle {
             font-size: 20px;
             color: #666;
             margin: 0;
             font-weight: 500;
         }
        .summary-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
            border: 1px solid #ddd;
        }
        .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
        }
        .summary-item {
            background: white;
            padding: 15px;
            border-radius: 6px;
            text-align: center;
            border: 1px solid #e0e0e0;
        }
        .summary-value {
            font-size: 24px;
            font-weight: bold;
            color: #c9a06c;
            margin-bottom: 5px;
        }
        .summary-label {
            font-size: 14px;
            color: #666;
        }
        .table-section {
            margin-top: 30px;
        }
        .section-title {
            font-size: 22px;
            font-weight: bold;
            color: #c9a06c;
            margin-bottom: 15px;
            text-align: center;
            background: #f8f9fa;
            padding: 10px;
            border-radius: 6px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            font-size: 14px;
        }
        th, td {
            border: 1px solid #ddd;
            padding: 8px 12px;
            text-align: right;
        }
        th {
            background: #c9a06c;
            color: white;
            font-weight: bold;
        }
        tbody tr:nth-child(even) {
            background: #f9f9f9;
        }
        tbody tr:nth-child(odd) {
            background: white;
        }
        .status-completed { color: #28a745; font-weight: bold; }
        .status-cancelled { color: #dc3545; font-weight: bold; }
        .status-pending { color: #ffc107; font-weight: bold; }
        .status-progress { color: #17a2b8; font-weight: bold; }
        .rating-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-weight: bold;
        }
        .rating-excellent { background: #d4edda; color: #155724; }
        .rating-good { background: #cce7ff; color: #0c5aa6; }
        .rating-average { background: #fff3cd; color: #856404; }
        .rating-poor { background: #f8d7da; color: #721c24; }
        .rating-none { background: #e2e3e5; color: #6c757d; }
    </style>
</head>
 <body>
     <div class="header-section">
         <div class="left-logo">
             ${appLogoBase64 ? `<img src="${appLogoBase64}" alt="شعار التطبيق" class="logo">` : ''}
         </div>
        <div class="center-title">
            <h1 class="main-title">تقرير ردود الأساتذة</h1>
            <p class="subtitle">${this.selectedTeacherName} - ${this.formattedDateRange}</p>
        </div>
        <div class="right-logo">
            ${orgLogo ? `<img src="${orgLogo}" alt="${this.selectedOrganization?.name || ''}" class="logo">` : ''}
        </div>
    </div>

    <div class="summary-section">
        <h3 class="section-title">ملخص الإحصائيات</h3>
        <div class="summary-grid">
            <div class="summary-item">
                <div class="summary-value">${this.summary.totalSessions}</div>
                <div class="summary-label">إجمالي الجلسات</div>
            </div>
            <div class="summary-item">
                <div class="summary-value">${this.summary.completedSessions}</div>
                <div class="summary-label">الجلسات المكتملة</div>
            </div>
            <div class="summary-item">
                <div class="summary-value">${this.summary.averageRating.toFixed(1)}</div>
                <div class="summary-label">متوسط التقييم</div>
            </div>
            <div class="summary-item">
                <div class="summary-value">${this.summary.totalStudents}</div>
                <div class="summary-label">عدد الدارسين</div>
            </div>
        </div>
    </div>

    <div class="table-section">
        <h3 class="section-title">تفاصيل الجلسات والردود</h3>
        <table>
            <thead>
                <tr>
                    <th>اسم الدارس</th>
                    <th>الهدف</th>
                    <th>الورد اليومي</th>
                    <th>الحالة</th>
                    <th>ملاحظات الشيخ</th>
                    <th>الوقت</th>
                    <th>التاريخ</th>
                    <th>التقييم</th>
                </tr>
            </thead>
            <tbody>`

             // Add all session data
       dataToExport.forEach(session => {
         const statusClass = this.getStatusHtmlClass(session.status)
         const ratingClass = this.getRatingHtmlClass(session.rating || 0)
         
         html += `
                 <tr>
                     <td>${session.studentName || ''}</td>
                     <td>${session.goalDescription || 'غير محدد'}</td>
                     <td>${session.pagesReviewed || 'غير محدد'}</td>
                     <td class="${statusClass}">${this.getStatusText(session.status)}</td>
                     <td>${session.teacherNotes || 'لا توجد ملاحظات'}</td>
                     <td>${this.formatTime(session.sessionTime)}</td>
                     <td>${this.formatDate(session.sessionDate)}</td>
                     <td><span class="rating-badge ${ratingClass}">${session.rating || 0}</span></td>
                 </tr>`
       })

      html += `
            </tbody>
        </table>
    </div>
</body>
</html>`
      
      return html
    },
    
    getStatusHtmlClass(status) {
      const classes = {
        'COMPLETED': 'status-completed',
        'CANCELLED': 'status-cancelled', 
        'PENDING': 'status-pending',
        'IN_PROGRESS': 'status-progress'
      }
      return classes[status] || ''
    },
    
    getRatingHtmlClass(rating) {
      if (rating >= 4.5) return 'rating-excellent'
      if (rating >= 3.5) return 'rating-good'
      if (rating >= 2.5) return 'rating-average'
      if (rating > 0) return 'rating-poor'
      return 'rating-none'
    },
    
    async getAllReportData() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.teacher_response_report, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
          params: {
            teacherId: this.selectedTeacherId,
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo,
            page: 0,
            size: 999999 // Get all data
          }
        })
        
        if (response.data.result.status === 200) {
          const data = response.data.result.data
          return data.sessions || []
        }
        return []
      } catch (error) {
        console.error('Error getting all report data:', error)
        return []
      }
    },
    
    async exportToExcel() {
      try {
        this.loading = true
        
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.teacher_response_report_csv, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
          params: {
            teacherId: this.selectedTeacherId,
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo
          },
          responseType: 'blob'
        })
        
        const blob = new Blob([response.data], { 
          type: 'text/csv; charset=utf-8' 
        })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `تقرير_ردود_${this.selectedTeacherName}_${this.filters.dateFrom}_${this.filters.dateTo}.csv`
        link.click()
        
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        console.error('Error exporting CSV:', error)
        this.error = 'فشل في تحميل ملف CSV'
      } finally {
        this.loading = false
      }
    },
    
    changePage(page) {
      if (page !== '...' && page !== this.pagination.currentPage) {
        this.pagination.currentPage = page
        this.generateReport()
      }
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ar-SA')
    },
    
    formatTime(timeString) {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getStatusClass(status) {
      const classes = {
        'COMPLETED': 'bg-green-100 text-green-800',
        'CANCELLED': 'bg-red-100 text-red-800',
        'PENDING': 'bg-yellow-100 text-yellow-800',
        'IN_PROGRESS': 'bg-blue-100 text-blue-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    },
    
    getStatusText(status) {
      const statusTexts = {
        'COMPLETED': 'مكتملة',
        'CANCELLED': 'ملغية',
        'PENDING': 'معلقة',
        'IN_PROGRESS': 'جارية'
      }
      return statusTexts[status] || 'غير محدد'
    },
    
    resetSummary() {
      return {
        totalSessions: 0,
        completedSessions: 0,
        averageRating: 0,
        totalStudents: 0
      }
    },
    
    // Rating Enhancement Methods
    getStarColor(starNumber, rating) {
      if (starNumber <= rating) {
        if (rating >= 4.5) return '#FFD700' // Gold for excellent
        if (rating >= 3.5) return '#FFA500' // Orange for good
        if (rating >= 2.5) return '#FFC107' // Yellow for average
        if (rating >= 1.5) return '#FF9800' // Dark orange for below average
        return '#FF5722' // Red for poor
      }
      return '#E0E0E0' // Gray for empty stars
    },
    
    getRatingBadgeClass(rating) {
      if (rating >= 4.5) return 'bg-green-100 text-green-800 border-green-300'
      if (rating >= 3.5) return 'bg-blue-100 text-blue-800 border-blue-300'
      if (rating >= 2.5) return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      if (rating >= 1.5) return 'bg-orange-100 text-orange-800 border-orange-300'
      if (rating > 0) return 'bg-red-100 text-red-800 border-red-300'
      return 'bg-gray-100 text-gray-600 border-gray-300'
    },
    
    getRatingDotClass(rating) {
      if (rating >= 4.5) return 'bg-green-500'
      if (rating >= 3.5) return 'bg-blue-500'
      if (rating >= 2.5) return 'bg-yellow-500'
      if (rating >= 1.5) return 'bg-orange-500'
      if (rating > 0) return 'bg-red-500'
      return 'bg-gray-400'
    },
    
    getRatingTextClass(rating) {
      if (rating >= 4.5) return 'text-green-700'
      if (rating >= 3.5) return 'text-blue-700'
      if (rating >= 2.5) return 'text-yellow-700'
      if (rating >= 1.5) return 'text-orange-700'
      if (rating > 0) return 'text-red-700'
      return 'text-gray-500'
    },
    
    getRatingText(rating) {
      if (rating >= 4.5) return 'ممتاز'
      if (rating >= 3.5) return 'جيد جداً'
      if (rating >= 2.5) return 'جيد'
      if (rating >= 1.5) return 'مقبول'
      if (rating > 0) return 'ضعيف'
      return 'غير مقيم'
    },
    
    async getImageAsBase64(imagePath) {
      try {
        return new Promise((resolve) => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = function() {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = this.naturalWidth
            canvas.height = this.naturalHeight
            ctx.drawImage(this, 0, 0)
            const dataURL = canvas.toDataURL('image/png')
            resolve(dataURL)
          }
          img.onerror = function() {
            console.warn('Could not load image:', imagePath)
            resolve('')
          }
          img.src = imagePath
        })
      } catch (error) {
        console.warn('Error converting image to base64:', error)
        return ''
      }
    },
    formatGregorian(dateStr) {
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
  },
}
}
</script>

<style scoped>
.teacher-response-report {
  font-family: 'Cairo', sans-serif;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* RTL Support */
[dir="rtl"] {
  text-align: right;
}

[dir="rtl"] .flex {
  flex-direction: row;
}

[dir="rtl"] .space-x-reverse > :not([hidden]) ~ :not([hidden]) {
  --tw-space-x-reverse: 1;
  margin-right: calc(0.5rem * var(--tw-space-x-reverse));
  margin-left: calc(0.5rem * calc(1 - var(--tw-space-x-reverse)));
}

/* Fix table cell alignment for RTL */
[dir="rtl"] td, [dir="rtl"] th {
  text-align: right;
}

/* Ensure buttons maintain proper spacing in RTL */
[dir="rtl"] .flex-row-reverse {
  flex-direction: row-reverse;
}

@media print {
  .no-print {
    display: none !important;
  }
}

/* PDF Header Styles */
.pdf-header {
  direction: rtl;
  margin-bottom: 15px;
}

.header-logos {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 60px;
}

.left-logo, .right-logo {
  flex: 0 0 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.center-title {
  flex: 1;
  text-align: center;
  padding: 0 15px;
}

.organization-logo, .app-logo {
  max-width: 80px;
  max-height: 80px;
  object-fit: contain;
  border-radius: 8px;
}

.pdf-export .organization-logo, .pdf-export .app-logo {
  max-width: 60px;
  max-height: 60px;
}

.pdf-title {
  font-size: 20px;
  font-weight: bold;
  color: #c9a06c;
  margin: 0 0 6px 0;
  font-family: 'Tajawal', sans-serif;
}

.pdf-export .pdf-title {
  font-size: 26px !important;
  margin: 0 0 4px 0 !important;
  font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
  direction: rtl !important;
  unicode-bidi: bidi-override !important;
}

.pdf-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0 0 3px 0;
  font-family: 'Tajawal', sans-serif;
}

.pdf-export .pdf-subtitle {
  font-size: 17px !important;
  margin: 0 !important;
  font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
  direction: rtl !important;
  unicode-bidi: bidi-override !important;
}

.header-divider {
  border: none;
  border-top: 1.5px solid #c9a06c;
  margin: 8px 0;
}

.pdf-export .header-divider {
  margin: 6px 0 !important;
}

.pdf-export .pdf-header {
  display: block !important;
  break-inside: avoid;
  page-break-inside: avoid;
}
</style> 